import _ from 'lodash'
import { initDataListStatus } from 'types/types'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type {
  GetMstCalendarCalendarGetRequest,
  GetMstCentersCenterGetRequest,
  GetMstCenterStockItemCenterStockItemGetRequest,
  GetMstCenterSupplierCenterSupplierGetRequest,
  GetMstCustomersCenterCustomerCenterGetRequest,
  GetMstCustomersCustomerGetRequest,
  GetMstItemsItemGetRequest,
  GetMstSuppliersSupplierGetRequest,
  GetMstTransportRelationTransportRelationGetRequest,
  GetOrderAlertOrderAlertGetRequest,
  GetShipExpectedShipExpectedGetRequest,
  GetStockStockGetRequest,
} from 'api-client'
import type { GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest } from 'hooks/useCenterSupplierCalendar'

export interface OrderPlanTransportLotRequest extends GetStockStockGetRequest {
  transportType?: object;
  localMinLoadCapacity?: number;
}

export interface UploadRecordStockDetailSearchParam {
  companyId: number
  centerIds: number[]
  orgSupplierIds: string[]
  itemCodes: string[]
  orgItemCodes: string[]
  searchDateRangeStartTypeCode?: string
  searchDateRangeEndTypeCode?: string
  searchDateRangeStartDate?: string
  searchDateRangeEndDate?: string
  searchWeekNum?: number
}

export type StockCenterSearchParam = GetStockStockGetRequest & { orgItemCodes?: string[] }

export interface SearchParamState {
  tenantId: string
  companyId: number
  searchParam: {
    top: GetOrderAlertOrderAlertGetRequest
    dashboard: GetStockStockGetRequest
    shipExpectedCenter: GetShipExpectedShipExpectedGetRequest
    shipExpectedItem: GetShipExpectedShipExpectedGetRequest
    stockCenter: StockCenterSearchParam
    orderPlanTransportLot: OrderPlanTransportLotRequest
    uploadRecordStockDetail: UploadRecordStockDetailSearchParam
    item: GetMstItemsItemGetRequest;
    masterItem: GetMstItemsItemGetRequest;
    calendar: GetMstCalendarCalendarGetRequest;
    calendarCenterSupplier: GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest;
    center: GetMstCentersCenterGetRequest;
    centerStockItem: GetMstCenterStockItemCenterStockItemGetRequest
    supplier: GetMstSuppliersSupplierGetRequest;
    centerSupplier: GetMstCenterSupplierCenterSupplierGetRequest;
    customerCenter: GetMstCustomersCenterCustomerCenterGetRequest;
    transport: GetMstTransportRelationTransportRelationGetRequest;
    customer: GetMstCustomersCustomerGetRequest;
  }
}

const SearchParamKey = {
  Top: 'top',
  Dashboard: 'dashboard',
  ShipExpectedCenter: 'shipExpectedCenter',
  ShipExpectedItem: 'shipExpectedItem',
  StockCenter: 'stockCenter',
  OrderPlanTransportLot: 'orderPlanTransportLot',
  UploadRecordStockDetail: 'uploadRecordStockDetail',
  Item: 'item',
  masterItem: 'masterItem',
  Calendar: 'calendar',
  CalendarCenterSupplier: 'calendarCenterSupplier',
  Center: 'center',
  CenterStockItem: 'centerStockItem',
  Supplier: 'supplier',
  Customer: 'customer',
  CenterSupplier: 'centerSupplier',
  Transport: 'transport',
  CustomerCenter: 'customerCenter'
} as const

const initSearchParam = {
  top: {
    centerId: 0,
    orgSupplierId: undefined,
    sort: [],
    limit: initDataListStatus().paginationModel.pageSize,
    pageNo: initDataListStatus().paginationModel.page,
  },
  dashboard: {
    companyId: 0,
    centerId: undefined,
    itemCode: '',
    dateRange: 'year',
  },
  shipExpectedCenter: {
    companyId: 0,
    centerId: undefined,
    viewDateType: '',
    viewUnitTsype: '',
    itemCode: '',
  },
  shipExpectedItem: {
    companyId: 0,
    centerId: undefined,
    viewDateType: '',
    viewUnitType: '',
    itemCode: '',
  },
  stockCenter: {
    companyId: 0,
    centerId: undefined,
    centerIds: [],
    viewUnitType: '',
    orgSupplierIds: [],
    itemCodes: [],
    orgItemCodes: [],
    optimizeRange: '',
    orderPlanFlg: undefined
  },
  orderPlanTransportLot: {
    companyId: 0,
    centerId: undefined,
    viewUnitType: 'case',
    localMinLoadCapacity: 0,
    orgSupplierId: '',
    itemCode: '',
    optimizeRange: '',
    orderPlanFlg: undefined
  } as OrderPlanTransportLotRequest,
  uploadRecordStockDetail: {
    companyId: 0,
    centerIds: [],
    orgSupplierIds: [],
    itemCodes: [],
    orgItemCodes: [],
    searchDateRangeStartTypeCode: undefined,
    searchDateRangeEndTypeCode: undefined,
    searchDateRangeStartDate: undefined,
    searchDateRangeEndDate: undefined,
    searchWeekNum: undefined,
  } as UploadRecordStockDetailSearchParam,
  item: {
    companyId: 0,
    itemCode: '',
    itemName: '',
    orgItemCode: '',
    barcode: '',
    orgSupplierId: undefined,
  },
  masterItem: {
    companyId: 0,
    orgSupplierId: '',
    itemCode: '',
    itemName: '',
  },
  calendar: {
    companyId: 0,
    orgCenterId: '',
  },
  calendarCenterSupplier: {
    companyId: 0,
    orgCenterId: '',
    orgSupplierId: '',
    getAllDates: false,
  },
  center: {
    companyId: 0,
  },
  centerStockItem: {
    companyId: 0,
    centerId: undefined,
    itemCode: '',
  },
  supplier: {
    companyId: 0,
  },
  customer: {
    companyId: 0,
    orgCustomerId: '',
  },
  centerSupplier: {
    companyId: 0,
  },
  customerCenter: {
    companyId: 0,
    orgCustomerId: '',
  },
  transport: {
    companyId: 0,
    orgCenterId: '',
    orgCustomerId: '',
  },
}

interface SearchParamStore {
  searchParamState: SearchParamState[]
  setSearchParamState: <T>(
    tenantId: string,
    companyId: number,
    searchParamKey: keyof typeof SearchParamKey,
    searchParam: T
  ) => void
}

const updateSearchParam = <T>(
  state: SearchParamState[],
  tenantId: string,
  companyId: number,
  searchParamKey: keyof typeof SearchParamKey,
  searchParam: T
) => {
  if (_.find(state, { tenantId, companyId }) === undefined) {
    return [
      ...state,
      {
        tenantId,
        companyId,
        searchParam: {
          ...initSearchParam,
          [`${SearchParamKey[searchParamKey]}`]: {
            ...searchParam,
          },
        },
      },
    ]
  }

  return _.map(state, (prevValue) => {
    if (prevValue.tenantId === tenantId && prevValue.companyId === companyId) {
      return {
        ...prevValue,
        searchParam: {
          ...prevValue.searchParam,
          [`${SearchParamKey[searchParamKey]}`]: {
            ...searchParam,
          },
        },
      }
    }

    return { ...prevValue }
  })
}

// NOTE: hooksのuserからcompanyIdを取得したいが、hooksはコンポーネントでしか呼び出すことができないため、初期値を0として
// 各page配下で初期値の設定を行う
// src/utils/getInitCompanyId.ts
export const useSearchParamStore = create<SearchParamStore>()(
  persist(
    (set) => ({
      searchParamState: [],
      setSearchParamState: <T>(
        tenantId: string,
        companyId: number,
        searchParamKey: keyof typeof SearchParamKey,
        searchParam: T
      ) => {
        set((state) => (
          {searchParamState: updateSearchParam<T>(
            state.searchParamState,
            tenantId,
            companyId,
            searchParamKey,
            searchParam
          ),
        }))
      },
    }),
    {
      // ローカルストレージの構造が変わる場合はversionとmigrateを更新することで新しいストアが更新される
      version: 11,
      name: 'search-param-storage',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      migrate: (persistedState: SearchParamStore, version) => {
        if (version === 1) {
          // eslint-disable-next-line no-param-reassign
          persistedState.searchParamState = []
        } else {
          // 検索キーが追加されている場合
          persistedState.searchParamState.forEach((param) => {
            const defkey = Object.entries(SearchParamKey)
              .filter(([key]) => !_.has(param.searchParam, key))
              .map(([key]) => key as keyof typeof SearchParamKey); 
          
            defkey.forEach((key) => {
              persistedState.setSearchParamState(
                param.tenantId,
                param.companyId,
                key, // key は keyof typeof SearchParamKey 型
                initSearchParam[SearchParamKey[key]]
              );
            });
          });
        }
        return persistedState
      },
    }
  )
)
